// =====================================================================
// CahiersDeCharge.jsx — Day 11 (28-05-2026)
// =====================================================================
// Liste projets (avec count fichiers) → click projet → liste fichiers.
// Admin (cahier.upload.all) peut uploader + supprimer ; tout user lit.
// =====================================================================

import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { cahiers as cahiersApi } from '../services/api.js';

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-BE', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

function iconForMime(mime) {
  if (mime?.startsWith('image/'))   return { txt: 'IMG', bg: '#DCFCE7', fg: '#16A34A' };
  if (mime === 'application/pdf')   return { txt: 'PDF', bg: '#FEE2E2', fg: '#dc2626' };
  if (mime?.includes('word'))       return { txt: 'DOCX', bg: '#DBEAFE', fg: '#2563EB' };
  if (mime?.includes('sheet') || mime?.includes('excel')) return { txt: 'XLSX', bg: '#DCFCE7', fg: '#15803D' };
  if (mime === 'text/plain')        return { txt: 'TXT', bg: '#F3F4F6', fg: '#374151' };
  return { txt: 'FILE', bg: '#E5E7EB', fg: '#6B7280' };
}

export default function CahiersDeCharge() {
  const { can } = useAuth();
  const canUpload = can('cahier.upload.all');

  const [projects, setProjects]               = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [files, setFiles]                     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [filesLoading, setFilesLoading]       = useState(false);
  const [uploading, setUploading]             = useState(false);
  const [error, setError]                     = useState(null);
  const [searchTerm, setSearchTerm]           = useState('');     // Day 11 — filtrage cartes

  // Day 11 — filter projects on the fly (name + kind, case-insensitive)
  const filteredProjects = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.kind || '').toLowerCase().includes(q)
    );
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await cahiersApi.listProjects();
        if (!cancelled) setProjects(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || 'Erreur chargement projets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function loadFiles(projectId) {
    setFilesLoading(true); setError(null);
    try {
      const { files } = await cahiersApi.listFiles(projectId);
      setFiles(files || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur chargement fichiers');
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }

  function openProject(p) {
    setSelectedProject(p);
    setFiles([]);
    loadFiles(p.id);
  }

  function backToList() {
    setSelectedProject(null);
    setFiles([]);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !selectedProject) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await cahiersApi.upload(selectedProject.id, fd);
      await loadFiles(selectedProject.id);
      // refresh project list count
      const data = await cahiersApi.listProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur upload');
    } finally {
      setUploading(false);
      e.target.value = ''; // reset file input
    }
  }

  async function handleDownload(file) {
    try {
      const res = await cahiersApi.download(file.id, false);
      const blob = new Blob([res.data], { type: file.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur téléchargement');
    }
  }

  // Day 11 — Voir inline (fetch en blob puis ouvre dans onglet, garde l'auth cookie)
  async function handleView(file) {
    try {
      const res = await cahiersApi.download(file.id, true); // inline=true
      const blob = new Blob([res.data], { type: file.mimeType });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      // libère après que l'onglet a chargé (5s)
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (!w) setError('Le navigateur a bloqué l\'ouverture. Autorisez les pop-ups.');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur consultation');
    }
  }

  // Day 11 — Raccourci depuis la carte projet : ouvre le fichier le plus récent inline
  async function handleConsultFromCard(e, project) {
    e.stopPropagation(); // évite d'ouvrir la vue détail en plus
    if (!project.fileCount) return;
    try {
      const { files } = await cahiersApi.listFiles(project.id);
      if (!files || files.length === 0) {
        setError('Aucun fichier pour ce projet.');
        return;
      }
      // files est trié uploadedAt desc — files[0] = le plus récent
      await handleView(files[0]);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur consultation');
    }
  }

  async function handleDelete(file) {
    if (!window.confirm(`Supprimer "${file.filename}" ?\nCette action est irréversible.`)) return;
    try {
      await cahiersApi.remove(file.id);
      await loadFiles(selectedProject.id);
      const data = await cahiersApi.listProjects();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur suppression');
    }
  }

  // ---- View 1: project list ----
  if (!selectedProject) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.h1}>📋 Cahiers de charge</h1>
          <p style={styles.sub}>Documents et procédures par projet. Lecture pour tous. Modification réservée aux administrateurs.</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {!loading && projects.length > 0 && (
          <div style={styles.searchWrap}>
            <input
              type="search"
              list="cahier-projects-list"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Rechercher un projet (nom ou type)…"
              style={styles.searchInput}
              autoFocus
            />
            <datalist id="cahier-projects-list">
              {projects.map((p) => (
                <option key={p.id} value={p.name} label={p.kind || ''} />
              ))}
            </datalist>
            <span style={styles.searchCount}>
              {filteredProjects.length} / {projects.length}
            </span>
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>Chargement…</div>
        ) : (
          <div style={styles.projectGrid}>
            {projects.length === 0 && <div style={styles.empty}>Aucun projet trouvé.</div>}
            {projects.length > 0 && filteredProjects.length === 0 && (
              <div style={styles.empty}>Aucun projet ne correspond à « {searchTerm} ».</div>
            )}
            {filteredProjects.map((p) => (
              <div key={p.id} style={styles.projectCard} onClick={() => openProject(p)}>
                <div style={styles.projectHeader}>
                  <span style={styles.projectName}>{p.name}</span>
                  {p.fileCount > 0 && (
                    <button
                      onClick={(e) => handleConsultFromCard(e, p)}
                      style={styles.cardViewBtn}
                      title={p.fileCount === 1 ? 'Consulter le document' : `Consulter le plus récent (${p.fileCount} fichiers)`}
                    >👁</button>
                  )}
                  {p.fileCount > 0 ? (
                    <span style={styles.badgeOk}>{p.fileCount} fichier{p.fileCount > 1 ? 's' : ''}</span>
                  ) : (
                    <span style={styles.badgeMiss}>Aucun</span>
                  )}
                </div>
                <div style={styles.projectMeta}>
                  <span style={styles.kind}>{p.kind || '—'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- View 2: files for selected project ----
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button onClick={backToList} style={styles.backBtn}>← Retour aux projets</button>
        <h1 style={styles.h1}>{selectedProject.name}</h1>
        <p style={styles.sub}>{selectedProject.kind || ''}</p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {canUpload && (
        <label style={styles.uploadZone}>
          <input type="file" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }}
                 accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt" />
          <div style={{ fontSize: 28, marginBottom: 6 }}>{uploading ? '⏳' : '⬆️'}</div>
          <div style={{ fontWeight: 600, color: '#714B67' }}>
            {uploading ? 'Upload en cours…' : 'Cliquer pour ajouter un fichier'}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            PDF, DOCX, XLSX, JPG, PNG, WEBP, TXT — max 20 MB
          </div>
        </label>
      )}

      {filesLoading ? (
        <div style={styles.loading}>Chargement fichiers…</div>
      ) : files.length === 0 ? (
        <div style={styles.empty}>
          {canUpload
            ? 'Aucun fichier pour ce projet. Uploadez le premier ci-dessus.'
            : 'Aucun cahier de charge disponible pour ce projet.'}
        </div>
      ) : (
        <div>
          {files.map((f) => {
            const ic = iconForMime(f.mimeType);
            return (
              <div key={f.id} style={styles.fileRow}>
                <div style={{ ...styles.fileIcon, background: ic.bg, color: ic.fg }}>{ic.txt}</div>
                <div style={styles.fileMeta}>
                  <div style={styles.fileName}>{f.filename}</div>
                  <div style={styles.fileSub}>
                    {fmtBytes(f.sizeBytes)} · uploadé {fmtDate(f.uploadedAt)}
                    {f.uploadedBy?.name && <> par {f.uploadedBy.name}</>}
                  </div>
                </div>
                <div style={styles.fileActions}>
                  <button onClick={() => handleView(f)} style={styles.btnView} title="Consulter dans le navigateur">👁 Voir</button>
                  <button onClick={() => handleDownload(f)} style={styles.btnDownload} title="Télécharger sur cet appareil">📥</button>
                  {canUpload && (
                    <button onClick={() => handleDelete(f)} style={styles.btnDelete} title="Supprimer">🗑</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 900, margin: '0 auto' },
  header: { marginBottom: 20 },
  h1: { margin: '0 0 4px', fontSize: '1.4rem', fontWeight: 700 },
  sub: { margin: 0, color: '#6b7280', fontSize: '0.88rem' },
  backBtn: {
    background: 'transparent', border: 0, color: '#714B67', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: 600, padding: '4px 0', marginBottom: 8,
  },
  loading: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  empty: { padding: 40, textAlign: 'center', color: '#9ca3af', background: '#fafbfc', borderRadius: 8, border: '1px dashed #e5e7eb' },
  error: {
    padding: 12, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5',
    borderRadius: 8, marginBottom: 14, fontSize: '0.88rem',
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
  },
  searchInput: {
    flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8,
    fontSize: '0.92rem', background: '#fff', outline: 'none',
  },
  searchCount: {
    fontSize: '0.78rem', color: '#9ca3af', fontVariantNumeric: 'tabular-nums',
    minWidth: 56, textAlign: 'right',
  },
  projectGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12,
  },
  projectCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: 14, cursor: 'pointer', transition: 'all 0.12s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  projectHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 },
  projectName: { fontWeight: 700, fontSize: '0.95rem', color: '#1f2937', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  projectMeta: { display: 'flex', alignItems: 'center', gap: 8 },
  kind: { fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 },
  cardViewBtn: {
    background: '#16A085', color: '#fff', border: 0, padding: '4px 8px',
    borderRadius: 6, cursor: 'pointer', fontSize: '0.95rem', lineHeight: 1, flex: 'none',
  },
  badgeOk: { background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 },
  badgeMiss: { background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 },
  uploadZone: {
    display: 'block', cursor: 'pointer', textAlign: 'center',
    background: '#fbf9fd', border: '2px dashed #c4b5d8', borderRadius: 10,
    padding: 22, marginBottom: 14, transition: 'background 0.12s',
  },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: 12, background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 8, marginBottom: 8,
  },
  fileIcon: {
    width: 38, height: 38, borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '0.7rem', flex: 'none',
  },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: { fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  fileSub: { fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 },
  fileActions: { display: 'flex', gap: 6, flex: 'none' },
  btnView: {
    background: '#16A085', color: '#fff', border: 0, padding: '6px 10px',
    borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
  },
  btnDownload: {
    background: '#714B67', color: '#fff', border: 0, padding: '6px 10px',
    borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
  },
  btnDelete: {
    background: '#fff', color: '#dc2626', border: '1px solid #fca5a5',
    padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem',
  },
};
